import { NgClass } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, effect, inject, input, SecurityContext, signal } from '@angular/core';
import { DomSanitizer } from '@angular/platform-browser';
import { RouterLink } from '@angular/router';
import { DesanitizeSlashPipe } from '../../../../security';
import { CachedUrlResolverPipe } from '../../../pipes';
import { IButtonBlok } from './button.model';
import { TailwindService } from '@seven1/angular-storyblok/tailwind';
import { RouterLinkPipe } from '@seven1/angular-storyblok-components/cta';

@Component({
    selector: 'app-button',
    imports: [RouterLink, DesanitizeSlashPipe, CachedUrlResolverPipe, NgClass, RouterLinkPipe],
    templateUrl: './button.component.html',
    styleUrls: ['./button.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    host: { '[class]': 'getContainerClasses()' },
})
export class ButtonComponent {
    private _tailwind = inject(TailwindService);
    private _sanitizer: DomSanitizer = inject(DomSanitizer);

    block = input.required<IButtonBlok>();
    sanitizedLink = signal<string | null>(null);

    getHoverShadows = computed<string[]>(() => {
        const block = this.block();
        const res = [];

        if (block.bgColor === 'primary') res.push('hover:shadow-button-primary');
        if (block.bgColorTablet === 'primary') res.push('md:hover:shadow-button-primary');
        if (block.bgColorDesktop === 'primary') res.push('lg:hover:shadow-button-primary');

        if (block.bgColor === 'secondary') res.push('hover:shadow-button-secondary');
        if (block.bgColorTablet === 'secondary') res.push('md:hover:shadow-button-secondary');
        if (block.bgColorDesktop === 'secondary') res.push('lg:hover:shadow-button-secondary');

        if (block.bgColor === 'accent') res.push('hover:shadow-button-accent');
        if (block.bgColorTablet === 'accent') res.push('md:hover:shadow-button-accent');
        if (block.bgColorDesktop === 'accent') res.push('lg:hover:shadow-button-accent');

        if (block.bgColor === 'grey') res.push('hover:shadow-button-grey');
        if (block.bgColorTablet === 'grey') res.push('md:hover:shadow-button-grey');
        if (block.bgColorDesktop === 'grey') res.push('lg:hover:shadow-button-grey');

        return res;
    });

    getContainerClasses = computed<string[]>(() => {
        const block = this.block();
        return [
            'flex',
            ...this._tailwind.getFlexboxClasses({
                flexJustify: block.align,
                flexJustifyTablet: block.alignTablet,
                flexJustifyDesktop: block.alignDesktop,
            }),
            ...this._tailwind.getDisplayClasses(block, 'flex'),
        ];
    });

    getOuterClasses = computed<string[]>(() => {
        const block = this.block();
        return [
            'block',
            'cursor-pointer',
            'whitespace-nowrap',
            ...this.getHoverShadows(),
            ...this._tailwind.getBorderRadiusClasses(block),
            ...this._tailwind.getBackgroundColorClasses(block),
            ...this._tailwind.getSpacingClasses(block),
        ];
    });

    getInnerClasses = computed<string[]>(() => {
        return this._tailwind.getTypographyClasses(this.block());
    });

    constructor() {
        effect(() => {
            const link = this.block().link;
            this.sanitizedLink.set(this._sanitizer.sanitize(SecurityContext.URL, link?.url || ''));
        });
    }
}
